import axios, { AxiosError } from "axios";

import { BACKEND_URL } from "@repo/core/constants/global";
import useAuthStore from "@repo/core/stores/auth";
import { logAxiosRequestError } from "@repo/core/services/observability/requestErrorLogger";
import {
  expireAuthenticatedSession,
  hasActiveSession,
  isUnauthorizedResponse,
  rejectStaleSessionResponse,
  startAuthenticatedRequest
} from "@repo/core/services/session/sessionExpiration";
import { SessionExpiredFlowError } from "@repo/core/services/session/sessionEpoch";

const apiInstance = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    "Content-Type": "application/json"
  }
});

export const shouldLogoutExpiredSession = hasActiveSession;

const isAuthenticationEndpoint = (url?: string): boolean =>
  (url || "").split("?")[0].replace(/\/+$/, "").endsWith("/token");

apiInstance.interceptors.request.use((config) => {
  if (!isAuthenticationEndpoint(config.url)) {
    startAuthenticatedRequest(config);
  }

  const { user } = useAuthStore.getState();

  if (user?.token || process.env.TEST_TOKEN) {
    config.headers.Authorization = `Bearer ${user?.token || process.env.TEST_TOKEN}`;
  }

  return config;
});

apiInstance.interceptors.response.use(
  (response) => {
    rejectStaleSessionResponse(response.config);

    if (isUnauthorizedResponse(response.status, response.data)) {
      const error = new AxiosError(
        "Session expired",
        AxiosError.ERR_BAD_RESPONSE,
        response.config,
        response.request,
        response
      );

      expireAuthenticatedSession({ apiClient: "api", error });
      logAxiosRequestError("api", error);

      return Promise.reject(error);
    }

    return response;
  },
  (err) => {
    if (err instanceof SessionExpiredFlowError) {
      return Promise.reject(err);
    }

    try {
      rejectStaleSessionResponse(err?.config);
    } catch (staleSessionError) {
      return Promise.reject(staleSessionError);
    }

    const { method, url, baseURL } = err?.config || {};
    const { status } = err?.response || {};
    if (__DEV__) {
      console.error(
        `[API][ERROR][${method?.toUpperCase()}] [${status}] ${JSON.stringify({
          baseURL,
          url,
          data: err?.response?.data,
          request: {
            data: err?.config?.data,
            params: err?.config?.params
          },
          response: err?.response?.data
        })}`
      );
    }

    if (isUnauthorizedResponse(err?.response?.status, err?.response?.data)) {
      expireAuthenticatedSession({ apiClient: "api", error: err });
    }

    logAxiosRequestError("api", err);

    return Promise.reject(err);
  }
);

export { apiInstance };
